"use client";
import * as React from "react";
import TextField, { TextFieldProps } from "@mui/material/TextField";
import Autocomplete, { AutocompleteProps } from "@mui/material/Autocomplete";
import clsx from "clsx";
import { AppOptionType, AppOptionTypeSchema } from "@/models";
import AppCheckbox from "./AppCheckbox";
import { Box } from "@mui/material";
import { forwardRef } from "react";
import AppChip from "./AppChip";
import AppFormControlLoading from "./form-control/AppFormControlLoading";

const ITEM_HEIGHT = 42;
const LISTBOX_PADDING = 8;
const MAX_VISIBLE_ITEMS = 8;
const OVERSCAN = 5;

export type AppVirtualizedComboboxProps = Partial<
  AutocompleteProps<any, any, any, any>
> & {
  options: AppOptionType[];
  label?: string;
  textFieldProps?: TextFieldProps;
  isLoading?: boolean;
};

const VirtualizedListbox = forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLElement>
>(function VirtualizedListbox(props, ref) {
  const { children, ...other } = props;
  const items = React.Children.toArray(children) as React.ReactElement[];
  const [scrollTop, setScrollTop] = React.useState(0);

  const itemCount = items.length;
  const containerHeight =
    Math.min(itemCount, MAX_VISIBLE_ITEMS) * ITEM_HEIGHT + LISTBOX_PADDING * 2;
  const totalHeight = itemCount * ITEM_HEIGHT;
  const startIndex = Math.max(
    0,
    Math.floor(scrollTop / ITEM_HEIGHT) - OVERSCAN,
  );
  const endIndex = Math.min(
    itemCount,
    startIndex + MAX_VISIBLE_ITEMS + OVERSCAN * 2,
  );
  const visibleItems = items.slice(startIndex, endIndex);

  return (
    <div
      ref={ref}
      {...other}
      onScroll={(event) =>
        setScrollTop((event.currentTarget as HTMLDivElement).scrollTop)
      }
      style={{
        maxHeight: containerHeight,
        overflowY: "auto",
      }}
    >
      <ul
        style={{
          position: "relative",
          height: totalHeight + LISTBOX_PADDING * 2,
          margin: 0,
          padding: 0,
        }}
      >
        {visibleItems.map((item, index) =>
          React.cloneElement(item, {
            style: {
              ...(item.props.style || {}),
              position: "absolute",
              top: (startIndex + index) * ITEM_HEIGHT + LISTBOX_PADDING,
              left: 0,
              right: 0,
            },
          }),
        )}
      </ul>
    </div>
  );
});

const AppVirtualizedCombobox = forwardRef(
  (
    {
      options,
      label,
      sx,
      className,
      textFieldProps,
      multiple,
      isLoading,
      size,
      ...rest
    }: AppVirtualizedComboboxProps,
    ref,
  ) => {
    return (
      <>
        {isLoading && <AppFormControlLoading size={size} />}
        {!isLoading && (
          <Autocomplete
            size={size}
            multiple={multiple}
            fullWidth
            disablePortal
            disableListWrap
            options={options}
            ListboxComponent={VirtualizedListbox}
            loadingText="Đang tải..."
            noOptionsText="Không có dữ liệu"
            clearText="Xóa"
            closeText="Đóng"
            className={clsx("rounded-2 ", className)}
            sx={{
              backgroundColor: "#fff",
              "&&Mui-expanded": {
                borderBottomLeftRadius: 0,
                borderBottomRightRadius: 0,
              },
              "&&Mui-expanded .MuiOutlinedInput-notchedOutline": {
                borderBottomLeftRadius: 0,
                borderBottomRightRadius: 0,
              },
              "&& .MuiInputBase-root": {
                paddingLeft: "4px",
                paddingTop: "10px",
                paddingBottom: "10px",
                fontSize: "14px",
                lineHeight: "18px",
              },
              "&& .MuiInputBase-root.MuiInputBase-sizeSmall": {
                paddingLeft: "4px",
                paddingTop: "6px",
                paddingBottom: "6px",
                fontSize: "14px",
                lineHeight: "18px",
              },
              ".MuiInputBase-root.MuiOutlinedInput-root.Mui-disabled": {
                backgroundColor: "disabled.main",
              },
              ".MuiInputBase-root.MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline":
                {
                  borderWidth: 0,
                },
              "&& .MuiInputLabel-root": {
                fontSize: "14px",
                lineHeight: "18px",
                padding: "0 4px",
                transform: "translate(8px, 6px) scale(1)",
              },
              "&& .MuiInputLabel-root.MuiInputLabel-shrink": {
                transform: "translate(8px, -9px) scale(0.75)",
              },
              "&& .MuiOutlinedInput-root  .MuiInputBase-input": {
                padding: "0px 4px",
                fontSize: "inherit",
                lineHeight: "20px",
                height: "20px",
              },
              "&& .MuiOutlinedInput-notchedOutline": {
                padding: "0 8px",
                border: "1px solid",
                borderRadius: "4px",
                borderColor: "border.secondary",
              },
              ".MuiInputBase-root.MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline":
                {
                  borderColor: "error.main",
                },
              "&& .MuiInputBase-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                {
                  border: "2px solid",
                  borderColor: "primary.main",
                },
              "& .MuiInputBase-root:hover .MuiOutlinedInput-notchedOutline": {
                border: "1px solid",
                borderColor: "primary.main",
              },
              "&& + .MuiAutocomplete-popper .MuiPaper-root .MuiAutocomplete-listbox":
                {
                  padding: 0,
                },
              "&& + .MuiAutocomplete-popper .MuiPaper-root": {
                borderBottomLeftRadius: "4px",
                borderBottomRightRadius: "4px",
                border: "unset",
              },
              "&& + .MuiAutocomplete-popper .MuiAutocomplete-listbox .MuiAutocomplete-option":
                {
                  fontWeight: "400",
                  fontSize: "14px",
                  lineHeight: "16px",
                  padding: "8px 12px",
                  border: "unset",
                },
              "&& + .MuiAutocomplete-popper .MuiAutocomplete-listbox .MuiAutocomplete-option.Mui-focused":
                {
                  backgroundColor: "var(--light)",
                  color: "var(--primary)",
                },
              "&& + .MuiAutocomplete-popper .MuiAutocomplete-noOptions": {
                textAlign: "left",
                padding: "8px 12px",
              },
              "&&.MuiAutocomplete-root.MuiAutocomplete-root .MuiInputLabel-root:not(.MuiInputLabel-shrink)":
                {
                  top: "5px",
                },
              ...sx,
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                inputRef={ref}
                label={label}
                {...textFieldProps}
              />
            )}
            getOptionLabel={(option) => {
              if (AppOptionTypeSchema.safeParse(option).success) {
                return option.label ?? "";
              }

              return options?.find((item) => item.value === option)?.label ?? "";
            }}
            isOptionEqualToValue={(option, value) => {
              if (AppOptionTypeSchema.safeParse(value).success) {
                return option.value === value.value;
              }

              return option.value === value;
            }}
            getOptionDisabled={(option) => Boolean(option?.disabled)}
            renderOption={multiple ? renderOptionMultiType : renderOption}
            renderTags={(tagValue, getTagProps) =>
              tagValue.map((option: AppOptionType, index) => (
                <AppChip
                  label={<>{option.label}</>}
                  {...getTagProps({ index })}
                  key={option.value}
                />
              ))
            }
            {...rest}
            value={rest?.value === "" ? null : rest?.value}
          />
        )}
      </>
    );
  },
);

AppVirtualizedCombobox.displayName = "AppVirtualizedCombobox";

export default React.memo(AppVirtualizedCombobox);

const renderOption = (
  props: React.HTMLAttributes<HTMLLIElement>,
  option: AppOptionType,
) => {
  return (
    <li
      {...props}
      key={option.value}
      style={{
        ...(props.style || {}),
        opacity: option.disabled ? 0.5 : 1,
        pointerEvents: option.disabled ? "none" : props.style?.pointerEvents,
      }}
    >
      <>{option.label}</>
    </li>
  );
};

const renderOptionMultiType = (
  props: React.HTMLAttributes<HTMLLIElement>,
  option: AppOptionType,
  { selected }: { selected: boolean },
) => {
  return (
    <li
      {...props}
      key={option.value}
      style={{
        ...(props.style || {}),
        opacity: option.disabled ? 0.5 : 1,
        pointerEvents: option.disabled ? "none" : props.style?.pointerEvents,
      }}
    >
      <Box>
        <AppCheckbox
          className="mr-2"
          checked={selected}
          disabled={option.disabled}
          label={<>{option.label}</>}
        />
      </Box>
    </li>
  );
};
